/* relativeTime.js — Unified operational momentum timestamps (iter-170).
 *
 * Single source of truth for "what changed recently?" timestamps across:
 *   • Sales Progression — Last movement column, expanded row timeline,
 *     row meta, exchange-ready cards, requires-attention rows.
 *   • Deal Room — Hero "Last updated" line, Workstreams momentum,
 *     Activity feed entries, timeline summaries, right-rail momentum.
 *
 * Doctrine
 * --------
 * Calm, premium, subtle. Never noisy, never social-media-style. Each
 * timestamp answers: "How fresh is this signal?" in <16 chars.
 *
 *   < 1 min   →  "Just now"
 *   < 60 min  →  "Xm ago"          ("18m ago")
 *   < 24h     →  "Xh ago"          ("2h ago")
 *   < 48h     →  "Yesterday"       (or "Yesterday, 14:32" with `withClock`)
 *   < 7d      →  "Xd ago"          ("4d ago")
 *   < 5w      →  "Xw ago"
 *   else      →  "DD MMM"          ("12 May") · adds year on rollover
 *
 * Public API
 * ----------
 *   relTimeFromIso(iso, {withClock?})   → "18m ago" | "Yesterday, 14:32" | "12 May"
 *   relTimeFromIsoCalm(iso)             → "Today" | "Yesterday" | "Xd ago" | "12 May"
 *   freshnessTone(iso)                  → "fresh" (<24h) | "recent" (<7d) | "stale" (≥7d)
 *   freshnessClass(tone)                → Tailwind tokens (text / dot)
 *   formatMomentumLine({title, iso, withClock?})
 *                                       → "Contract received · 18m ago"
 *
 * NB: Sub-minute granularity intentionally rounds to "Just now" rather than
 *     surfacing seconds — operational pressure isn't time-on-task tracking.
 */

const _MS_MIN  = 60_000;
const _MS_HOUR = 3_600_000;
const _MS_DAY  = 86_400_000;

const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const _two = (n) => (n < 10 ? `0${n}` : String(n));

/** Calm "Today, HH:MM" / "Yesterday, HH:MM" / "Xd ago" / "12 May" — the
 *  shape used by the Sales Progression "Last movement" column. */
export function relTimeFromIso(iso, opts = {}) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const now = Date.now();
  const diff = now - t;
  if (diff < _MS_MIN) return "Just now";
  if (diff < _MS_HOUR) {
    const m = Math.max(1, Math.round(diff / _MS_MIN));
    return `${m}m ago`;
  }
  if (diff < _MS_DAY) {
    const h = Math.max(1, Math.round(diff / _MS_HOUR));
    return `${h}h ago`;
  }
  const d = Math.floor(diff / _MS_DAY);
  const dt = new Date(t);
  const hm = `${_two(dt.getHours())}:${_two(dt.getMinutes())}`;
  if (d === 1) return opts.withClock ? `Yesterday, ${hm}` : "Yesterday";
  if (d < 7)  return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5)  return `${w}w ago`;
  // > 5w — surface the calendar date so the eye can read it directly.
  const sameYear = dt.getFullYear() === new Date(now).getFullYear();
  const base = `${dt.getDate()} ${_MONTHS[dt.getMonth()]}`;
  if (!sameYear) return `${base} ${dt.getFullYear()}`;
  return opts.withClock ? `${base}, ${hm}` : base;
}

/** Even calmer variant — collapses <24h into "Today" so the timeline
 *  reads as a date-group header rather than minute-granular metadata. */
export function relTimeFromIsoCalm(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  if (diff < _MS_DAY) return "Today";
  return relTimeFromIso(iso);
}

/** Freshness band — purely for tonal hierarchy on momentum surfaces.
 *  Reuses the calm doctrine from waitDuration (never red, never noisy).
 *
 *    fresh  — moved within the last 24h
 *    recent — moved in the last 7 days
 *    stale  — older or missing
 */
export function freshnessTone(iso) {
  if (!iso) return "stale";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "stale";
  const diff = Date.now() - t;
  if (diff < _MS_DAY)    return "fresh";
  if (diff < _MS_DAY * 7) return "recent";
  return "stale";
}

export const FRESHNESS_CLASSES = Object.freeze({
  fresh:  { text: "text-emerald-700", dot: "bg-emerald-500" },
  recent: { text: "text-slate-600",   dot: "bg-slate-400"   },
  stale:  { text: "text-slate-400",   dot: "bg-slate-300"   },
});

export function freshnessClass(tone) {
  return FRESHNESS_CLASSES[tone] || FRESHNESS_CLASSES.recent;
}

/** Compose a calm momentum line — the canonical shape for any
 *  "Last movement" / "Most recent activity" / "Recently changed"
 *  callsite. Returns a single string the surface can drop in directly.
 *
 *      formatMomentumLine({ title: "Contract received", iso }) →
 *        "Contract received · 18m ago"
 *
 *  When `iso` is missing the function returns just the title; the
 *  surface should hide the row entirely if it shouldn't render at all.
 */
export function formatMomentumLine({ title, iso, withClock }) {
  if (!title) return "";
  const when = iso ? relTimeFromIso(iso, { withClock }) : "";
  return when ? `${title} · ${when}` : title;
}
